import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarDots, ShoppingCart, NotePencil, Heartbeat, ClipboardText, CheckCircle, ArrowRight, Clock, Pill, Drop, CalendarCheck, BookOpen, Baby, ListChecks, Warning, Leaf, Egg, Carrot, Orange, Bell, Target, Check } from '@phosphor-icons/react';
import { useNavigate, Link } from 'react-router-dom';
import { Md } from '@/components/Md';
import { isBabyPatient, isGravidaPatient, isPosPartoPatient, isPreConcPatient } from '@/lib/utils';
import PushPermissionPrompt from '@/components/PushPermissionPrompt';
import WeightWeeklyAverageCard from '@/components/WeightWeeklyAverageCard';
import { fmtDay } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const GROUP_ICONS = { energia: Leaf, ferro_proteina: Egg, horticolas: Carrot, gordura: Drop, micronutrientes: Orange };
const BABY_MEAL_LABELS = { pequeno_almoco: 'Pequeno-almoço', meio_manha: 'Meio da manhã', almoco: 'Almoço', meio_tarde: 'Meio da tarde', jantar: 'Jantar', ceia: 'Ceia' };
const ICON_MAP = { baby: Baby, checklist: ListChecks, diary: NotePencil, content: BookOpen, calendar: CalendarDots, checkin: Heartbeat, cart: ShoppingCart };

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const [dRes, obRes] = await Promise.all([
        axios.get(`${API}/api/smart-dashboard`),
        axios.get(`${API}/api/onboarding/status`).catch(() => ({ data: null })),
      ]);
      setD(dRes.data);
      setOnboarding(obRes.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  const firstName = user?.name?.split(' ')[0] || '';
  const isBaby = isBabyPatient(d);
  const isGravida = isGravidaPatient(d);
  const isPosParto = isPosPartoPatient(d);
  const isPreConc = isPreConcPatient(d);
  const alerts = d?.alerts || [];
  const highAlerts = alerts.filter(a => a.priority === 'high');
  const otherAlerts = alerts.filter(a => a.priority !== 'high');
  const nextMeal = d?.next_meal;
  const quickActions = d?.quick_actions || [];
  const seasonal = d?.seasonal_suggestions || [];

  // Calculate gestational/postpartum/baby weeks
  const calcWeeks = (dateStr) => {
    if (!dateStr) return null;
    try { const w = Math.floor((new Date() - new Date(dateStr)) / (7 * 24 * 60 * 60 * 1000)); return w >= 0 ? w : null; } catch { return null; }
  };
  // Gravida: weeks since DUM (gestational age)
  const gestWeeks = isGravida && user?.dum ? calcWeeks(user.dum) : null;
  // Pós-parto: prefer delivery_date (real birth) over baby_birth_date/dum
  const ppRef = user?.delivery_date || user?.baby_birth_date || user?.dum;
  const ppWeeks = isPosParto ? calcWeeks(ppRef) : null;
  // Build a human-readable pós-parto label (used in header/info banner)
  const ppLabel = (() => {
    if (ppWeeks == null) return null;
    if (ppWeeks < 12) return `${ppWeeks} ${ppWeeks === 1 ? 'semana' : 'semanas'} pós-parto`;
    if (ppWeeks < 52) {
      const months = Math.floor(ppWeeks / 4.345);
      const remainingW = Math.round(ppWeeks - months * 4.345);
      return remainingW > 0
        ? `${months} ${months === 1 ? 'mês' : 'meses'} e ${remainingW} ${remainingW === 1 ? 'semana' : 'semanas'} pós-parto`
        : `${months} ${months === 1 ? 'mês' : 'meses'} pós-parto`;
    }
    const years = Math.floor(ppWeeks / 52);
    return `${years} ${years === 1 ? 'ano' : 'anos'} pós-parto`;
  })();
  // Baby/intro alimentar: weeks since baby birth
  const babyWeeks = isBaby ? calcWeeks(user?.baby_birth_date) : null;

  return (
    <div className="fade-in" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="mb-5">
        <h2 className="font-heading font-medium text-2xl text-brand-text" data-testid="dashboard-greeting">
          Olá, {firstName}
        </h2>
        <p className="text-sm text-brand-muted mt-1">
          {d?.texts?.greeting || (isBaby ? 'Como está o dia do bebé?' : 'Como está a correr o teu dia?')}
        </p>
        {d?.texts?.motivation && <p className="text-xs text-brand-green/70 mt-1 italic">{d.texts.motivation}</p>}
      </div>

      {/* Push notification prompt (soft banner, dismissable) */}
      <div className="mb-4"><PushPermissionPrompt /></div>

      {/* Weekly weight average */}
      <div className="mb-4"><WeightWeeklyAverageCard /></div>

      {/* Program-specific info */}
      {isGravida && gestWeeks !== null && (
        <div className="bg-brand-mint/10 border border-brand-mint/30 rounded-2xl p-4 mb-4" data-testid="gravida-info">
          <p className="text-sm font-medium text-brand-green">Estás na semana {gestWeeks} de gestação</p>
          <div className="flex gap-2 mt-3">
            {[{l:'Ementa',p:'/ementa'},{l:'Plano',p:'/programa'},{l:'Suplementação',p:'/suplementacao'}].map(a => (
              <Link key={a.p} to={a.p} className="flex-1 bg-white border border-brand-border rounded-xl py-2 text-xs font-medium text-brand-text hover:border-brand-green/30 text-center no-underline">{a.l}</Link>
            ))}
          </div>
        </div>
      )}
      {isPosParto && ppWeeks !== null && (
        <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-4 mb-4" data-testid="posparto-info">
          <p className="text-sm font-medium text-brand-green" data-testid="posparto-label">Estás {ppLabel}</p>
          {!d?.checkin_done && (user?.followup_state || 'ativo') === 'ativo' && <p className="text-xs text-brand-gold-hover mt-1">Check-in semanal por fazer</p>}
          <div className="flex gap-2 mt-3">
            {[{l:'Ementa',p:'/ementa'},{l:'Plano',p:'/programa'},{l:'Check-in',p:'/checkin'}].map(a => (
              <Link key={a.p} to={a.p} className="flex-1 bg-white border border-brand-border rounded-xl py-2 text-xs font-medium text-brand-text hover:border-brand-green/30 text-center no-underline">{a.l}</Link>
            ))}
          </div>
        </div>
      )}
      {isPreConc && (
        <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-2xl p-4 mb-4" data-testid="preconc-info">
          <div className="flex gap-2 mt-1">
            {[{l:'Ementa',p:'/ementa'},{l:'Plano',p:'/programa'},{l:'Ciclo',p:'/ciclo'}].map(a => (
              <Link key={a.p} to={a.p} className="flex-1 bg-white border border-brand-border rounded-xl py-2 text-xs font-medium text-brand-text hover:border-brand-green/30 text-center no-underline">{a.l}</Link>
            ))}
          </div>
        </div>
      )}

      {/* Pre-consultation questionnaire alert */}
      {d?.pre_consult_pending && (
        <Link to="/pre-consulta" className="w-full bg-brand-gold/15 border border-brand-gold/30 rounded-3xl p-5 mb-5 text-left block no-underline text-inherit" data-testid="pre-consult-alert">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-1">Questionário pré-consulta</h3>
          <p className="text-xs text-brand-muted">Preenche antes da tua consulta para que possamos preparar tudo.</p>
          <span className="inline-block mt-2 bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium">Preencher questionário</span>
        </Link>
      )}

      {/* Onboarding card */}
      {onboarding && !onboarding.completed && onboarding.total_steps > 0 && (
        <div className="bg-brand-gold/15 border border-brand-gold/30 rounded-3xl p-5 mb-5" data-testid="onboarding-card">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-1">O teu onboarding</h3>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 bg-white/50 rounded-full h-2"><div className="bg-brand-gold h-2 rounded-full" style={{ width: `${(onboarding.completed_steps / onboarding.total_steps) * 100}%` }} /></div>
            <span className="text-[10px] text-brand-muted">{onboarding.completed_steps}/{onboarding.total_steps}</span>
          </div>
          <Link to="/onboarding" className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium no-underline" data-testid="continuar-onboarding">Continuar onboarding</Link>
        </div>
      )}
      {onboarding && onboarding.completed && (
        <Link to="/onboarding" className="w-full text-left bg-brand-bg/50 rounded-xl px-3 py-2 mb-3 text-[10px] text-brand-muted hover:bg-brand-bg block no-underline" data-testid="rever-conteudos">Onboarding concluído. <span className="text-brand-green">Rever conteúdos</span></Link>
      )}

      {/* Status pills */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {d?.checkin_done && <span className="bg-brand-green/10 text-brand-green text-[10px] font-medium px-3 py-1.5 rounded-full flex items-center gap-1 whitespace-nowrap"><CheckCircle size={12} weight="fill" /> Check-in feito</span>}
        {d?.diary_today_count > 0 && <span className="bg-brand-mint/20 text-brand-green text-[10px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap">{d.diary_today_count} registo{d.diary_today_count > 1 ? 's' : ''} hoje</span>}
        {d?.weekly_adherence > 0 && <span className="bg-purple-50 text-purple-500 text-[10px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap">{d.weekly_adherence}/7 adesão</span>}
        {d?.goals_done > 0 && <span className="bg-brand-gold/10 text-brand-gold-hover text-[10px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap">{d.goals_done}/{d.goals_total} metas</span>}
      </div>

      {/* High priority alerts */}
      {highAlerts.length > 0 && (
        <div className="space-y-2 mb-5">
          {highAlerts.map((a, i) => (
            <Link key={i} to={a.path} className="w-full flex items-center gap-3 p-3 bg-brand-green/5 border border-brand-green/20 rounded-2xl hover:bg-brand-green/10 transition-all text-left no-underline text-inherit" data-testid={`alert-${a.type}`}>
              {a.type === 'intro' ? <Baby size={18} className="text-brand-green" weight="duotone" /> : <Warning size={18} className="text-brand-gold" weight="duotone" />}
              <Md as="span" className="text-sm text-brand-text flex-1">{a.text}</Md>
              <ArrowRight size={14} className="text-brand-green" />
            </Link>
          ))}
        </div>
      )}

      {/* Next meal highlight */}
      {nextMeal && (
        <div className="bg-white border border-brand-green/20 rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.06)]" data-testid="next-meal-card">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardText size={16} className="text-brand-green" weight="duotone" />
            <h3 className="font-heading font-medium text-sm text-brand-text">
              {isBaby ? 'Hoje na ementa do bebé' : 'Próxima refeição'}
            </h3>
            {nextMeal.week_label && <span className="text-[9px] text-brand-muted bg-brand-bg px-2 py-0.5 rounded-full">{nextMeal.week_label}</span>}
          </div>
          {isBaby && nextMeal.meals ? (
            <div className="space-y-2">
              {nextMeal.meals.slice(0, 3).map((meal, i) => (
                <div key={i} className="bg-brand-bg/50 rounded-xl p-3">
                  <p className="text-xs font-medium text-brand-green mb-1">{BABY_MEAL_LABELS[meal.type] || meal.type}</p>
                  {(meal.items || []).map((item, j) => (
                    <p key={j} className="text-sm text-brand-text">{item.food}</p>
                  ))}
                  {meal.tip && <p className="text-[10px] text-brand-muted mt-1 italic">{meal.tip}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-brand-bg/50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs font-medium text-brand-green">{nextMeal.label}</p>
                {nextMeal.time && <span className="text-[10px] text-brand-muted">{nextMeal.time}</span>}
              </div>
              {(nextMeal.foods || []).map((f, i) => (
                <p key={i} className="text-sm text-brand-text">· {f}</p>
              ))}
            </div>
          )}
          <Link to="/diario" className="mt-3 text-xs text-brand-green font-medium hover:underline flex items-center gap-1 no-underline">
            Registar no diário <ArrowRight size={12} />
          </Link>
        </div>
      )}

      {/* Seasonal suggestions (baby only) */}
      {isBaby && seasonal.length > 0 && (
        <div className="bg-white border border-brand-gold/20 rounded-3xl p-5 mb-5" data-testid="seasonal-suggestions-card">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-3 flex items-center gap-2">
            <Leaf size={16} className="text-brand-gold" weight="duotone" />
            Alimentos da Época
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {seasonal.map((s, i) => {
              const Icon = GROUP_ICONS[s.group] || Leaf;
              return (
                <div key={i} className="bg-brand-bg/50 rounded-xl p-3" data-testid={`seasonal-${i}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={12} className="text-brand-green" weight="duotone" />
                    <span className="text-sm font-medium text-brand-text">{s.food_name}</span>
                  </div>
                  <p className="text-[10px] text-brand-muted leading-relaxed">{s.explanation}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weekly progress */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white border border-brand-border rounded-2xl p-4 text-center" data-testid="adherence-card">
          <p className="text-2xl font-heading font-semibold text-brand-green">{d?.weekly_adherence || 0}<span className="text-sm font-normal text-brand-muted">/7</span></p>
          <p className="text-[10px] text-brand-muted mt-1">Adesão semanal</p>
        </div>
        {d?.next_appointment && (
          <div className="bg-white border border-brand-green/15 rounded-2xl p-4" data-testid="next-appointment">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Próxima consulta</p>
            <p className="text-sm font-heading font-semibold text-brand-text">{d.next_appointment.date}</p>
            {d.next_appointment.time && <span className="text-xs text-brand-muted flex items-center gap-0.5 mt-0.5"><Clock size={11} />{d.next_appointment.time}</span>}
          </div>
        )}
      </div>

      {/* Other alerts */}
      {otherAlerts.length > 0 && (
        <div className="space-y-1.5 mb-5">
          {otherAlerts.map((a, i) => (
            <Link key={i} to={a.path} className="w-full flex items-center gap-3 p-2.5 bg-brand-bg/50 rounded-xl hover:bg-brand-bg transition-all text-left no-underline text-inherit">
              <Md as="span" className="text-xs text-brand-muted flex-1">{a.text}</Md>
              <ArrowRight size={12} className="text-brand-placeholder" />
            </Link>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        {quickActions.map((qa, i) => {
          const Icon = ICON_MAP[qa.icon] || CalendarDots;
          return (
            <Link key={i} to={qa.path} className="bg-white border border-brand-border rounded-2xl p-3 flex flex-col items-center gap-1.5 hover:border-brand-green/30 transition-all no-underline text-inherit" data-testid={`quick-${qa.icon}`}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-brand-green/10 text-brand-green"><Icon size={18} weight="duotone" /></div>
              <span className="text-[10px] font-medium text-brand-text text-center">{qa.label}</span>
            </Link>
          );
        })}
      </div>

      {/* End of client dashboard content */}

      {/* As minhas metas */}
      <MyGoalsCard />

      {/* Próximas consultas */}
      <UpcomingAppointmentsCard />

      {/* Notifications */}
      {(d?.notifications || []).length > 0 && (
        <div className="space-y-2 mb-5" data-testid="dashboard-notifications">
          {d.notifications.map((n, i) => (
            <div key={i} className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-3 flex items-start gap-2">
              <Bell size={16} className="text-brand-green flex-shrink-0 mt-0.5" weight="duotone" />
              <div className="flex-1">
                <p className="text-xs font-medium text-brand-text">{n.name}</p>
                <p className="text-[10px] text-brand-muted mt-0.5">{n.app_message || n.email_message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Consultation info */}
      {(d?.consultations_total > 0 || d?.program_start_date || d?.program_end_date) && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="consultation-info">
          {d?.consultations_total > 0 && (
            <div className="text-center mb-1">
              <p className="text-2xl font-heading font-semibold text-brand-green" data-testid="consultations-count">
                {d.consultations_done || 0} <span className="text-base font-normal text-brand-muted">de {d.consultations_total}</span>
              </p>
              <p className="text-[10px] text-brand-muted">Consultas do programa</p>
            </div>
          )}
          {(d?.program_start_date || d?.program_end_date) && (
            <div className={`flex items-center justify-center gap-4 text-center ${d?.consultations_total > 0 ? 'mt-3 pt-3 border-t border-brand-border' : ''}`} data-testid="program-dates">
              {d?.program_start_date && (
                <div>
                  <p className="text-xs font-medium text-brand-text">{fmtDay(d.program_start_date)}</p>
                  <p className="text-[10px] text-brand-muted">Início do programa</p>
                </div>
              )}
              {d?.program_end_date && (
                <div>
                  <p className="text-xs font-medium text-brand-text">{fmtDay(d.program_end_date)}</p>
                  <p className="text-[10px] text-brand-muted">Válido até</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Supplements */}
      {(d?.supplements || []).length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2 flex items-center gap-1"><Pill size={13} /> Suplementação</h3>
          {d.supplements.map((s, i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-brand-text">{s.name}, {s.dose}</span>
              {s.time && <span className="text-xs text-brand-muted">{s.time}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MY GOALS CARD ───
function MyGoalsCard() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const todayStr = new Date().toISOString().split('T')[0];

  const loadAll = useCallback(async () => {
    try {
      const [gRes, lRes] = await Promise.all([
        axios.get(`${API}/api/my-behavioral-goals`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/goal-logs`).catch(() => ({ data: [] })),
      ]);
      setGoals(Array.isArray(gRes.data) ? gRes.data : []);
      setLogs(Array.isArray(lRes.data) ? lRes.data : []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const isDoneToday = (goalId) => logs.some(l => l.goal_id === goalId && l.date === todayStr && l.status === 'done');

  const calcStreak = (goalId) => {
    const set = new Set(logs.filter(l => l.goal_id === goalId && l.status === 'done').map(l => l.date));
    let streak = 0;
    const cursor = new Date();
    // If today not done, allow streak based on yesterday onward (don't penalise)
    if (!set.has(cursor.toISOString().split('T')[0])) cursor.setDate(cursor.getDate() - 1);
    while (set.has(cursor.toISOString().split('T')[0])) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
    return streak;
  };

  const last7Days = (() => {
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      out.push(d.toISOString().split('T')[0]);
    }
    return out;
  })();

  const markDone = async (goalId) => {
    try {
      await axios.post(`${API}/api/goal-logs`, { goal_id: goalId, date: todayStr, status: 'done' });
      loadAll();
    } catch {}
  };

  if (loading) return null;

  return (
    <div className="mb-5" data-testid="my-goals-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-brand-green" weight="duotone" />
          <h3 className="font-heading font-medium text-sm text-brand-text">As minhas metas</h3>
        </div>
        {goals.length > 0 && (
          <Link to="/metas" className="text-[10px] text-brand-muted hover:text-brand-green flex items-center gap-0.5 no-underline" data-testid="ver-metas">
            Ver todas <ArrowRight size={10} />
          </Link>
        )}
      </div>
      {goals.length === 0 ? (
        <div className="bg-brand-bg/30 border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-xs text-brand-muted">A tua nutricionista ainda não definiu metas para ti.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {goals.slice(0, 3).map(g => {
            const done = isDoneToday(g.id);
            return (
              <div key={g.id} className="bg-white border border-brand-border rounded-2xl p-3" data-testid={`goal-row-${g.id}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => !done && markDone(g.id)}
                    disabled={done}
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${done ? 'bg-brand-green text-white' : 'bg-brand-green/10 text-brand-green hover:bg-brand-green/20'}`}
                    data-testid={`goal-check-${g.id}`}
                    title={done ? 'Cumprido hoje' : 'Marcar como cumprido'}
                  >
                    <Check size={14} weight="bold" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-brand-text">{g.title || 'Meta'}</p>
                      {(() => { const s = calcStreak(g.id); return s >= 2 ? (
                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium" data-testid={`goal-streak-${g.id}`}>🔥 {s} dias seguidos</span>
                      ) : null; })()}
                    </div>
                    {g.description && <p className="text-[11px] text-brand-muted mt-0.5 line-clamp-2">{g.description}</p>}
                    {/* 7-day strip */}
                    <div className="flex items-center gap-1 mt-2">
                      {last7Days.map(date => {
                        const isHit = logs.some(l => l.goal_id === g.id && l.date === date && l.status === 'done');
                        const isToday = date === todayStr;
                        const dayLabel = new Date(date).toLocaleDateString('pt-PT', { weekday: 'narrow' });
                        return (
                          <div key={date} className="flex flex-col items-center" title={date}>
                            <div className={`w-4 h-4 rounded-full ${isHit ? 'bg-brand-green' : 'bg-brand-bg border border-brand-border'} ${isToday ? 'ring-2 ring-brand-green/30' : ''}`} />
                            <span className="text-[8px] text-brand-placeholder mt-0.5">{dayLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── UPCOMING APPOINTMENTS CARD ───
function UpcomingAppointmentsCard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/my-appointments`)
      .then(({ data }) => setList(Array.isArray(data) ? data : []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  const upcoming = list.slice(0, 2);

  return (
    <div className="mb-5" data-testid="upcoming-appointments-card">
      <div className="flex items-center gap-2 mb-2">
        <CalendarCheck size={14} className="text-brand-green" weight="duotone" />
        <h3 className="font-heading font-medium text-sm text-brand-text">Próximas consultas</h3>
      </div>
      {upcoming.length === 0 ? (
        <div className="bg-brand-bg/30 border border-brand-border rounded-2xl p-4 text-center">
          <p className="text-xs text-brand-muted">Não tens consultas agendadas.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {upcoming.map(apt => (
            <div key={apt.id} className="bg-white border border-brand-green/15 rounded-2xl p-3 flex items-center gap-3" data-testid={`upcoming-apt-${apt.id}`}>
              <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                <CalendarDots size={16} className="text-brand-green" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-text">{apt.date || '-'}{apt.time ? ` · ${apt.time}` : ''}</p>
                <p className="text-[11px] text-brand-muted truncate">{apt.type || apt.location || 'Consulta'}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
